import React, { useEffect, useRef, useState } from 'react';
import { JsBridge, messageMethodIsSupport } from '@/utils/JsBridge';
import BookingAppUpgradeNotice, { isBookingAppUpgradeRequired } from './components/BookingAppUpgradeNotice';
import './styles/common.css';

const BOOKING_NATIVE_NAV_BODY_CLASS = 'booking-native-nav-mode';
const BRAND_TITLES = new Set(['奇鹿', '绘芽']);

function getNavBarTitle(): string {
  const navTitle = document.querySelector('.adm-nav-bar-title')?.textContent?.trim();
  return navTitle || '';
}

function getResolvedPageTitle(): string {
  const navBarTitle = getNavBarTitle();
  if (navBarTitle) {
    return navBarTitle;
  }

  const currentTitle = document.title.trim();
  if (currentTitle && !BRAND_TITLES.has(currentTitle)) {
    return currentTitle;
  }

  return '';
}

function detectInContainerNavMode(): boolean {
  if (typeof document !== 'undefined' && document.body?.classList.contains(BOOKING_NATIVE_NAV_BODY_CLASS)) {
    return true;
  }
  // Only hide the H5 navigation after the native navigation bridge is
  // actually available. ArkWeb can expose the App UA before JavaScript
  // channels are mounted, so `isInApp()` alone leaves the page with neither
  // an H5 header nor a native header on some Harmony devices.
  return messageMethodIsSupport('setNavigationBar') === true;
}

const BookingRouteShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [inContainerNavMode, setInContainerNavMode] = useState(() => detectInContainerNavMode());
  const lastPayloadRef = useRef('');
  const appUpgradeRequired = isBookingAppUpgradeRequired();

  useEffect(() => {
    if (!appUpgradeRequired) {
      return;
    }

    document.title = '升级奇鹿 App';
    if (messageMethodIsSupport('setNavigationBar')) {
      JsBridge.setNavigationBar(JSON.stringify({
        visible: true,
        title: '升级奇鹿 App',
      }));
    }
  }, [appUpgradeRequired]);

  useEffect(() => {
    if (inContainerNavMode) {
      return undefined;
    }

    const syncContainerNavMode = () => {
      if (detectInContainerNavMode()) {
        setInContainerNavMode(true);
        return true;
      }
      return false;
    };

    if (syncContainerNavMode()) {
      return undefined;
    }

    let attempts = 0;
    const maxAttempts = 40;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (syncContainerNavMode() || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 250);

    window.addEventListener('message', syncContainerNavMode);
    window.addEventListener('focus', syncContainerNavMode);
    window.addEventListener('pageshow', syncContainerNavMode);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('message', syncContainerNavMode);
      window.removeEventListener('focus', syncContainerNavMode);
      window.removeEventListener('pageshow', syncContainerNavMode);
    };
  }, [inContainerNavMode]);

  useEffect(() => {
    if (!inContainerNavMode || !document.body) {
      return undefined;
    }

    document.body.classList.add(BOOKING_NATIVE_NAV_BODY_CLASS);

    const syncNativeNav = () => {
      if (!messageMethodIsSupport('setNavigationBar')) {
        return;
      }

      const resolvedTitle = getResolvedPageTitle();
      if (!resolvedTitle) {
        return;
      }

      if (document.title !== resolvedTitle) {
        document.title = resolvedTitle;
      }

      const payload = JSON.stringify({
        visible: true,
        title: resolvedTitle,
      });

      if (payload === lastPayloadRef.current) {
        return;
      }

      lastPayloadRef.current = payload;
      JsBridge.setNavigationBar(payload);
    };

    const scheduleSync = () => {
      window.requestAnimationFrame(syncNativeNav);
    };

    scheduleSync();

    const observer = new MutationObserver(scheduleSync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const titleElement = document.querySelector('title');
    if (titleElement) {
      observer.observe(titleElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    window.addEventListener('focus', scheduleSync);
    window.addEventListener('pageshow', scheduleSync);

    return () => {
      observer.disconnect();
      document.body.classList.remove(BOOKING_NATIVE_NAV_BODY_CLASS);
      window.removeEventListener('focus', scheduleSync);
      window.removeEventListener('pageshow', scheduleSync);
      lastPayloadRef.current = '';
    };
  }, [inContainerNavMode]);

  if (appUpgradeRequired) {
    return <BookingAppUpgradeNotice />;
  }

  return <div className="booking-route-shell">{children}</div>;
};

export default BookingRouteShell;
